import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import { Shift } from "../models/Shift";
import { getShifts } from "../repositories/ShiftRepository";

type Props = {
	label?: string;
	value: number | null;
	onChange?: (id: number | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	productionDate: Date | null;
};

export const useShifts = (productionDate: Date | null) => {
	return useQuery<Shift[], Error>({
		queryKey: ["shifts", productionDate],
		queryFn: () => getShifts(productionDate),
	});
};

export const ShiftSelector = ({
	label = "Shift",
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	productionDate,
}: Props) => {
	const [error, setError] = useState("");

	const {
		data: shifts,
		isLoading,
		isError,
		error: e,
	} = useShifts(productionDate);

	const selectedShift =
		shifts?.find((type) => type.CurrentShiftId === value) || null;

	useEffect(() => {
		if (isError) {
			setError(e.message);
		}
	}, [isError, e]);

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				clearOnEscape
				options={shifts || []}
				getOptionLabel={(option) => option.CurrentShift}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>{params.InputProps.endAdornment}</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedShift}
				onChange={(event, newValue) => {
					onChange(newValue?.CurrentShiftId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading shifts"
			/>
		</>
	);
};
